// src/context/CartContext.jsx (Updated for LocalStorage)
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { daysInclusiveOslo, toOsloDateString } from '@/lib/osloDates';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Define keys for localStorage
const CART_STORAGE_KEY = 'dagslysQuickRentCart';
const USAGE_PERIOD_STORAGE_KEY = 'dagslysQuickRentUsagePeriod'; // Legacy key
const RENTAL_DATES_STORAGE_KEY = 'dagslysQuickRentRentalDates'; // Legacy key
const CHECKOUT_DATE_STORAGE_KEY = 'dagslysQuickRentCheckoutDate';
const CHECKIN_DATE_STORAGE_KEY = 'dagslysQuickRentCheckinDate';
const SHOOT_START_DATE_STORAGE_KEY = 'dagslysQuickRentShootStartDate';
const SHOOT_END_DATE_STORAGE_KEY = 'dagslysQuickRentShootEndDate';

// Rental factor table based on provided image, extended to 200 days
const rentalFactors = [
  { from: 1, to: 1, factor: 1 },
  { from: 2, to: 2, factor: 2 },
  { from: 3, to: 7, factor: 3 },
  { from: 8, to: 8, factor: 4 },
  { from: 9, to: 9, factor: 5 },
  { from: 10, to: 14, factor: 6 },
  { from: 15, to: 15, factor: 7 },
  { from: 16, to: 16, factor: 8 },
  { from: 17, to: 21, factor: 9 },
  { from: 22, to: 22, factor: 10 },
  { from: 23, to: 23, factor: 11 },
  { from: 24, to: 28, factor: 12 },
  { from: 29, to: 29, factor: 13 },
  { from: 30, to: 30, factor: 14 },
  { from: 31, to: 35, factor: 15 },
  { from: 36, to: 36, factor: 16 },
  { from: 37, to: 37, factor: 17 },
  { from: 38, to: 42, factor: 18 },
  { from: 43, to: 43, factor: 19 },
  { from: 44, to: 44, factor: 20 },
  { from: 45, to: 49, factor: 21 },
  { from: 50, to: 50, factor: 22 },
  { from: 51, to: 51, factor: 23 },
  { from: 52, to: 56, factor: 24 },
  { from: 57, to: 57, factor: 25 },
  { from: 58, to: 58, factor: 26 },
  { from: 59, to: 63, factor: 27 },
  { from: 64, to: 64, factor: 28 },
  { from: 65, to: 65, factor: 29 },
  { from: 66, to: 70, factor: 30 },
  { from: 71, to: 71, factor: 31 },
  { from: 72, to: 72, factor: 32 },
  { from: 73, to: 77, factor: 33 },
  { from: 78, to: 78, factor: 34 },
  { from: 79, to: 79, factor: 35 },
  { from: 80, to: 84, factor: 36 },
  { from: 85, to: 85, factor: 37 },
  { from: 86, to: 86, factor: 38 },
  { from: 87, to: 91, factor: 39 },
  { from: 92, to: 92, factor: 40 },
  { from: 93, to: 93, factor: 41 },
  { from: 94, to: 98, factor: 42 },
  { from: 99, to: 99, factor: 43 },
  { from: 100, to: 100, factor: 44 },
  { from: 101, to: 105, factor: 45 },
  { from: 106, to: 106, factor: 46 },
  { from: 107, to: 107, factor: 47 },
  { from: 108, to: 112, factor: 48 },
  { from: 113, to: 113, factor: 49 },
  { from: 114, to: 114, factor: 50 },
  { from: 115, to: 119, factor: 51 },
  { from: 120, to: 120, factor: 52 },
  { from: 121, to: 121, factor: 53 },
  { from: 122, to: 126, factor: 54 },
  { from: 127, to: 127, factor: 55 },
  { from: 128, to: 128, factor: 56 },
  { from: 129, to: 133, factor: 57 },
  { from: 134, to: 134, factor: 58 },
  { from: 135, to: 135, factor: 59 },
  { from: 136, to: 140, factor: 60 },
  { from: 141, to: 141, factor: 61 },
  { from: 142, to: 142, factor: 62 },
  { from: 143, to: 147, factor: 63 },
  { from: 148, to: 148, factor: 64 },
  { from: 149, to: 149, factor: 65 },
  { from: 150, to: 150, factor: 66 },
  { from: 151, to: 155, factor: 67 },
  { from: 156, to: 156, factor: 68 },
  { from: 157, to: 157, factor: 69 },
  { from: 158, to: 162, factor: 70 },
  { from: 163, to: 163, factor: 71 },
  { from: 164, to: 164, factor: 72 },
  { from: 165, to: 169, factor: 73 },
  { from: 170, to: 170, factor: 74 },
  { from: 171, to: 171, factor: 75 },
  { from: 172, to: 176, factor: 76 },
  { from: 177, to: 177, factor: 77 },
  { from: 178, to: 178, factor: 78 },
  { from: 179, to: 183, factor: 79 },
  { from: 184, to: 184, factor: 80 },
  { from: 185, to: 185, factor: 81 },
  { from: 186, to: 190, factor: 82 },
  { from: 191, to: 191, factor: 83 },
  { from: 192, to: 192, factor: 84 },
  { from: 193, to: 197, factor: 85 },
  { from: 198, to: 198, factor: 86 },
  { from: 199, to: 199, factor: 87 },
  { from: 200, to: 200, factor: 88 },
];

// Helper function to get rental factor based on days
const getRentalFactor = (days) => {
  if (!days || days <= 0) return 1; // Always default to 1 if days is 0 or invalid
  for (const entry of rentalFactors) {
    if (days >= entry.from && days <= entry.to) {
      return entry.factor;
    }
  }
  // If days exceed the last range, use the last factor
  return rentalFactors[rentalFactors.length - 1].factor;
};

const parseLegacyRange = (storageKey) => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.from || !parsed?.to) return null;
    const from = toOsloDateString(parsed.from);
    const to = toOsloDateString(parsed.to);
    if (!from || !to) return null;
    return { from, to };
  } catch (error) {
    console.error(`Failed to parse legacy date range from ${storageKey}`, error);
    return null;
  }
};

export const CartProvider = ({ children }) => {
  // Initialize cart state by reading from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        return JSON.parse(storedCart);
      }
    } catch (error) {
      console.error("Failed to parse cart from localStorage", error);
    }
    return {};
  });
  const previousCartRef = useRef({});

  const legacyUsagePeriod = parseLegacyRange(USAGE_PERIOD_STORAGE_KEY);
  const legacyRentalDates = parseLegacyRange(RENTAL_DATES_STORAGE_KEY);

  const [checkoutDate, setCheckoutDate] = useState(() => {
    const stored = localStorage.getItem(CHECKOUT_DATE_STORAGE_KEY);
    return stored || legacyRentalDates?.from || null;
  });
  const [checkinDate, setCheckinDate] = useState(() => {
    const stored = localStorage.getItem(CHECKIN_DATE_STORAGE_KEY);
    return stored || legacyRentalDates?.to || null;
  });
  const [shootStartDate, setShootStartDate] = useState(() => {
    const stored = localStorage.getItem(SHOOT_START_DATE_STORAGE_KEY);
    return stored || legacyUsagePeriod?.from || null;
  });
  const [shootEndDate, setShootEndDate] = useState(() => {
    const stored = localStorage.getItem(SHOOT_END_DATE_STORAGE_KEY);
    return stored || legacyUsagePeriod?.to || null;
  });

  // Calculate days of rent from Oslo calendar dates
  const daysOfRent = useMemo(() => {
    return daysInclusiveOslo(shootStartDate, shootEndDate);
  }, [shootStartDate, shootEndDate]);

  // Calculate rental factor based on daysOfRent
  const rentalFactor = useMemo(() => getRentalFactor(daysOfRent), [daysOfRent]);

  // Calculate price with rental factor
  const calculatePriceWithFactor = useCallback((price, quantity = 1) => {
    return price * quantity * rentalFactor;
  }, [rentalFactor]);

  // Calculate total price with rental factor
  const getCartTotalPriceWithFactor = useCallback(() => {
    return Object.values(cart).reduce((total, item) => {
      const price = Number(item.details?.price) || 0;
      return total + calculatePriceWithFactor(price, item.quantity);
    }, 0);
  }, [cart, calculatePriceWithFactor]);

  // Calculate tax amount (25%)
  const getTaxAmount = useCallback(() => {
    return Math.round(getCartTotalPriceWithFactor() * 0.25);
  }, [getCartTotalPriceWithFactor]);

  // Calculate protection amount (5%)
  const getProtectionAmount = useCallback(() => {
    return Math.round(getCartTotalPriceWithFactor() * 0.05);
  }, [getCartTotalPriceWithFactor]);

  // Calculate grand total (rounded to 1 decimal place)
  const getGrandTotal = useCallback(() => {
    const total = getCartTotalPriceWithFactor() + getTaxAmount() + getProtectionAmount();
    return Math.round(total * 10) / 10;
  }, [getCartTotalPriceWithFactor, getTaxAmount, getProtectionAmount]);

  // --- Effect to SAVE cart to localStorage whenever it changes ---
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error("Failed to save cart to localStorage", error);
    }
  }, [cart]);

  // Debug logging: prints full equipment object when cart entries are added/updated.
  useEffect(() => {
    const previousCart = previousCartRef.current || {};

    Object.entries(cart).forEach(([itemId, currentEntry]) => {
      const previousEntry = previousCart[itemId];
      const quantityChanged = (previousEntry?.quantity || 0) !== (currentEntry?.quantity || 0);
      const isNewEntry = !previousEntry;

      if ((isNewEntry || quantityChanged) && currentEntry?.details) {
        const equipment = currentEntry.details;
        const defaultGroupName =
          (typeof equipment.defaultGroup === "string" && equipment.defaultGroup.trim()) ||
          (typeof equipment.defaultgroup === "string" && equipment.defaultgroup.trim()) ||
          equipment.defaultGroup?.name ||
          equipment.defaultGroup?.displayname ||
          equipment.defaultGroup?.displayName ||
          equipment.defaultGroupName ||
          "Uncategorized";
        console.log("[CartContext] Cart item added/updated:", {
          itemId,
          previousQuantity: previousEntry?.quantity || 0,
          currentQuantity: currentEntry.quantity || 0,
          defaultGroupName,
          defaultgroup: equipment.defaultgroup || null,
          equipment,
        });
      }
    });

    previousCartRef.current = cart;
  }, [cart]);

  // --- Persist new single-date fields ---
  useEffect(() => {
    try {
      if (checkoutDate) {
        localStorage.setItem(CHECKOUT_DATE_STORAGE_KEY, checkoutDate);
      } else {
        localStorage.removeItem(CHECKOUT_DATE_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save checkout date to localStorage", error);
    }
  }, [checkoutDate]);

  useEffect(() => {
    try {
      if (checkinDate) {
        localStorage.setItem(CHECKIN_DATE_STORAGE_KEY, checkinDate);
      } else {
        localStorage.removeItem(CHECKIN_DATE_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save checkin date to localStorage", error);
    }
  }, [checkinDate]);

  useEffect(() => {
    try {
      if (shootStartDate) {
        localStorage.setItem(SHOOT_START_DATE_STORAGE_KEY, shootStartDate);
      } else {
        localStorage.removeItem(SHOOT_START_DATE_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save shoot start date to localStorage", error);
    }
  }, [shootStartDate]);

  useEffect(() => {
    try {
      if (shootEndDate) {
        localStorage.setItem(SHOOT_END_DATE_STORAGE_KEY, shootEndDate);
      } else {
        localStorage.removeItem(SHOOT_END_DATE_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save shoot end date to localStorage", error);
    }
  }, [shootEndDate]);

  // --- Cart modification functions ---
  const addToCart = useCallback((item, quantity = 1) => {
    if (!item || !item.id) {
      console.error("addToCart requires an item object with an id.");
      return;
    }
    console.log("[CartContext] Adding equipment to cart:", {
      item,
      quantityToAdd: quantity,
    });
    const itemId = item.id;
    setCart(prevCart => {
      const existingItem = prevCart[itemId];
      const newQuantity = (existingItem ? existingItem.quantity : 0) + quantity;
      console.log("[CartContext] Cart entry after add:", {
        itemId,
        previousQuantity: existingItem?.quantity || 0,
        newQuantity,
        equipment: item,
      });
      if (newQuantity <= 0) {
          const newCart = { ...prevCart };
          delete newCart[itemId];
          return newCart;
      }
      return {
        ...prevCart,
        [itemId]: {
          details: item,
          quantity: newQuantity
        }
      };
    });
  }, []);

  const removeFromCart = useCallback((itemId, quantity = 1) => {
    setCart(prevCart => {
      const newCart = { ...prevCart };
      const existingItem = newCart[itemId];
      if (existingItem) {
        const newQuantity = Math.max(0, existingItem.quantity - quantity);
        if (newQuantity === 0) {
          delete newCart[itemId];
        } else {
          newCart[itemId] = { ...existingItem, quantity: newQuantity };
        }
      }
      return newCart;
    });
  }, []);

  const removeItemCompletely = useCallback((itemId) => {
    setCart(prevCart => {
        if (itemId === null) {
            return {}; // Clear entire cart
        }
        const newCart = { ...prevCart };
        delete newCart[itemId];
        return newCart;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
  }, []);

  // --- Calculation functions ---
  const getTotalItemCount = useCallback(() => {
    return Object.values(cart).reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const getCartTotalPrice = useCallback(() => {
    return Object.values(cart).reduce((total, item) => {
        const price = Number(item.details?.price) || 0;
        return total + (price * item.quantity);
    }, 0);
  }, [cart]);

  const cartItems = useMemo(() => {
      return Object.values(cart).map(cartEntry => ({
          ...cartEntry.details,
          quantity: cartEntry.quantity,
          itemRemark: cartEntry.details?.itemRemark || ""
      }));
  }, [cart]);

  // --- Value provided by context ---
  const value = {
    cart,
    setCart,
    cartItems,
    addToCart,
    removeFromCart,
    removeItemCompletely,
    clearCart,
    getTotalItemCount,
    getCartTotalPrice,
    getCartTotalPriceWithFactor,
    calculatePriceWithFactor,
    getTaxAmount,
    getProtectionAmount,
    getGrandTotal,
    daysOfRent,
    rentalFactor,
    checkoutDate,
    setCheckoutDate,
    checkinDate,
    setCheckinDate,
    shootStartDate,
    setShootStartDate,
    shootEndDate,
    setShootEndDate,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};